import moment from 'moment-timezone';
import { dealUntilDate, createSubMeetings } from './time';
import { getRepeatRateOptions } from './tx-time';
import { isArray } from './types';
import { divideNum, makePhoneLink, downloadFile } from './util';
import { dealLiName, getInviteList, getLocalName } from './../user-center/util';
import { getICalendarDescPhoneInfoI18n } from './../i18n/utils/page';

const IS_I18N = process.env.IS_I18N === 'true';

/**
 * PMI会议参数转化
 * @param options
 * @returns {any}
 */
const dealCalendarParamsForPmi = (options = {}) => {
  const startTime = new Date();
  const opts = Object.assign({}, {
    uid: (`${startTime.getTime()}_${Math.floor(Math.random() * 1000)}`),
    prodid: '-//meeting.tencent.com//iCalendar',
    summary: '',
    description: null,
    location: null,
    userid: '',
    nickName: '',
    userType: 2, // 帐号类型 1 个人用户 2 企业用户
  }, options);

  return opts;
};

/**
 * 接口文档: https://mozilla-comm.github.io/ical.js/api/ICAL.Binary.html#.fromString
 */
export const loadIcalJs = () => new Promise((resolve, reject) => {
  if (!window.ICAL) {
    import(/* webpackChunkName: "ical" */ 'ical.js').then((res) => {
      resolve(res.default);
    }).catch((err) => {
      reject(err);
    });
  } else {
    resolve(window.ICAL);
  }
});

export const getICalendarDescPhoneInfo = (options = {}, t) => {
  const opts = dealICalendarParams(options);
  let $phone1 = '';
  let $phone2 = '';

  // 默认地区
  const defaultAreaInfo = opts.oneDial[opts.oneDial.default_area] || {};
  if (makePhoneLink(opts.oneDial, opts.oneDial.default_area, opts.meetingCode, opts.password, false)) {
    $phone1 = `${makePhoneLink(opts.oneDial, opts.oneDial.default_area, opts.meetingCode, opts.password, false)} (${t('中国大陆')})\n`;
  }
  if (defaultAreaInfo.extension) { // 转分号
    $phone2 = `${defaultAreaInfo.switchboard} ${t('转')} ${defaultAreaInfo.extension} (${t('中国大陆')})\n`;
  } else if (defaultAreaInfo.switchboard) {
    $phone2 = `${defaultAreaInfo.switchboard} (${t('中国大陆')})\n`;
  }
  // 中国香港
  const hongkongChinaInfo = opts.oneDial.hongkong_china;
  if (opts.oneDial && hongkongChinaInfo) {
    $phone1 += `${makePhoneLink(opts.oneDial, 'hongkong_china', opts.meetingCode, opts.password, false)} (${t('中国香港')})\n`;
    if (hongkongChinaInfo.extension) { // 转分号
      $phone2 += (`${hongkongChinaInfo.switchboard} ${t('转')} ${hongkongChinaInfo.extension} (${t('中国香港')})\n`);
    } else {
      $phone2 += (`${hongkongChinaInfo.switchboard} (${t('中国香港')})\n`);
    }
  }
  // 美国
  const usaInfo = opts.oneDial.usa;
  if (opts.oneDial && usaInfo) {
    $phone1 += `${makePhoneLink(opts.oneDial, 'usa', opts.meetingCode, opts.password, false)} (${t('美国')})\n`;
    if (usaInfo.extension) { // 转分号
      $phone2 += (`${usaInfo.switchboard} ${t('转')} ${usaInfo.extension} (${t('美国')})\n`);
    } else {
      $phone2 += (`${usaInfo.switchboard} (${t('美国')})\n`);
    }
  }

  let phoneInfo = '';
  if ($phone1) {
    phoneInfo += `${t('手机一键拨号入会')}\n${$phone1}\n`;
  }
  if ($phone2) {
    phoneInfo += `${t('根据您的位置拨号')}\n${$phone2}`;
  }

  return phoneInfo;
};

/**
 * PMI会议电话入会信息
 * @param options
 * @returns {string}
 */
export const getCalendarDescPhoneInfoForPmi = (options = {}, t) => {
  const opts = dealCalendarParamsForPmi(options);
  let $phone1 = '';
  let $phone2 = '';

  // 默认地区
  const defaultAreaInfo = opts.oneDial[opts.oneDial.default_area] || {};
  if (makePhoneLink(opts.oneDial, opts.oneDial.default_area, opts.pmiCode, opts.password, false)) {
    $phone1 = `${makePhoneLink(opts.oneDial, opts.oneDial.default_area, opts.pmiCode, opts.password, false)} (${t('中国大陆')})\n`;
  }
  if (defaultAreaInfo.extension) { // 转分号
    $phone2 = `${defaultAreaInfo.switchboard} ${t('转')} ${defaultAreaInfo.extension} (${t('中国大陆')})\n`;
  } else if (defaultAreaInfo.switchboard) {
    $phone2 = `${defaultAreaInfo.switchboard} (${t('中国大陆')})\n`;
  }
  // 中国香港
  const hongkongChinaInfo = opts.oneDial.hongkong_china;
  if (opts.oneDial && hongkongChinaInfo) {
    $phone1 += `${makePhoneLink(opts.oneDial, 'hongkong_china', opts.pmiCode, opts.password, false)} (${t('中国香港')})\n`;
    if (hongkongChinaInfo.extension) { // 转分号
      $phone2 += (`${hongkongChinaInfo.switchboard} ${t('转')} ${hongkongChinaInfo.extension} (${t('中国香港')})\n`);
    } else {
      $phone2 += (`${hongkongChinaInfo.switchboard} (${t('中国香港')})\n`);
    }
  }
  // 美国
  const usaInfo = opts.oneDial.usa;
  if (opts.oneDial && usaInfo) {
    $phone1 += `${makePhoneLink(opts.oneDial, 'usa', opts.pmiCode, opts.password, false)} (${t('美国')})\n`;
    if (usaInfo.extension) { // 转分号
      $phone2 += (`${usaInfo.switchboard} ${t('转')} ${usaInfo.extension} (${t('美国')})\n`);
    } else {
      $phone2 += (`${usaInfo.switchboard} (${t('美国')})\n`);
    }
  }

  let phoneInfo = '';
  if ($phone1) {
    phoneInfo = `${phoneInfo}${t('手机一键拨号入会')}\n${$phone1}\n`;
  }
  if ($phone2) {
    phoneInfo = `${phoneInfo}${t('根据您的位置拨号')}\n${$phone2}`;
  }
  return phoneInfo;
};

export const getICalendarDescriptionInPrivate = (options = {}) => {
  const opts = dealICalendarParams(options);
  const tmpPassword = opts.password ? `\n会议密码：${opts.password}` : '';
  const tmpLocation = opts.location ? `\n会议地点：${opts.location}` : '';
  const tmpLiveUrl = opts.liveUrl ? `直播链接：\n${opts.liveUrl}\n\n` : '';
  const tmpInviteList = getInviteList(opts.hostAppUid, opts.inviteList, Number(opts.userType));
  const tmpLocalName = getLocalName(opts.userid, opts.hostAppUid, opts.inviteList);
  // 会议时间
  let tmpTime = `${opts.startDate.Format('yyyy/MM/dd hh:mm')}-${opts.endDate.Format('hh:mm')}`;
  if (opts.startDate.getDate() !== opts.endDate.getDate()) { // 不是同一天
    tmpTime = `${opts.startDate.Format('yyyy/MM/dd hh:mm')}-${opts.endDate.Format('yyyy/MM/dd hh:mm')}`;
  }
  let _tmpUserName = `${opts.userid}${tmpLocalName}`;
  console.log('>>> oneDial:', opts.oneDial);
  if (typeof opts.oneDial === 'undefined') {
    return;
  }

  let _members = `\n与会成员：${tmpInviteList}`;
  if (Number(opts.userType) === 1) { // 帐号类型 1 个人用户 2 企业用户
    _tmpUserName = `${opts.nickName}`;
    _members = '';
  }
  // let _phoneInfo = getICalendarDescPhoneInfo(options);
  console.log('>>> _tmpUserName:', !_tmpUserName, !tmpInviteList, tmpInviteList);
  let _title = _tmpUserName + t(' 邀请您参加腾讯会议\n');
  if (!_tmpUserName) {
    _title = '';
  }
  if (!tmpInviteList) {
    _members = '';
  }

  const description = `${_title
  }会议主题：${opts.summary}\n`
    + `会议时间：${tmpTime}\n\n`
    + `点击链接直接加入会议：\n${opts.url}\n\n`
    + `${tmpLiveUrl}`
    + `会议 ID：${divideNum(opts.meetingCode)}${tmpPassword}\n\n`
    // _phoneInfo +
    + `${_members}`
    + `${tmpLocation}`;

  console.log('>>> get-iCalendar-description:\n', description);
  return description;
};

/**
 * PMI会议邀请详情
 * @param options
 * @returns {string}
 */
export const getCalendarDescriptionForPmi = (options = {}, t = v => v) => {
  const opts = dealCalendarParamsForPmi(options);
  const tmpPassword = opts.password ? `\n${t('会议密码')}: ${opts.password}` : '';
  console.log('>>> oneDial:', opts.oneDial);
  if (typeof opts.oneDial === 'undefined') {
    return;
  }
  const _phoneInfo = IS_I18N
    ? getICalendarDescPhoneInfoI18n(options.userPstnInfo, t) : getCalendarDescPhoneInfoForPmi(options, t);

  const description = `${`${t('欢迎参加腾讯会议')}\n\n`
    + `${t('仅1.3.5及以上版本才可加入该会议')}\n`
    + `${t('会议 ID')}: ${divideNum(opts.pmiCode)}`
    + `${tmpPassword}\n\n`
    + `${t('点击链接直接加入会议')}: \n${opts.url}\n\n`}${_phoneInfo
  }\n${t('最新版本下载地址')}: \n${process.env.IS_I18N === 'true' ? 'https://voovmeeting.com/download.html' : 'https://meeting.tencent.com/download.html'}`;

  // console.log('>>> get ICalendar Description:\n', description);
  return description;
};

const dealICalendarParams = (options = {}) => {
  const startTime = options.startDate || new Date();
  const endTime = options.endDate || new Date(startTime.getTime() + (1000 * 60 * 60)); // 默认持续一小时
  const opts = Object.assign({}, {
    uid: (`${startTime.getTime()}_${Math.floor(Math.random() * 1000)}`),
    prodid: '-//meeting.tencent.com//iCalendar',
    summary: '',
    description: null,
    location: null,
    password: '',
    pstnPassword: '',
    userid: '',
    nickName: '',
    email: '',
    chineseName: '',
    userType: 2, // 帐号类型 1 个人用户 2 企业用户
    startDate: startTime,
    endDate: endTime,
    meetingType: 0, // 0普通会议，1周期性会议，默认为0
    recurringRule: {},
    showPhoneInfo: true, //  是否显示电话入会信息
    inviteContent: '', // 后台接口返回的 会议详情信息
  }, options);

  return opts;
};

export const getICalendarDescMeetingTime = (opts = {}) => {
  let tmpTime = '';

  // 0普通会议，1周期性会议，默认为0
  if (opts.meetingType === 1) {
    tmpTime = `${opts.startDate.Format('hh:mm')}-${opts.endDate.Format('hh:mm')}`;
  } else {
    let gmtTime = '';
    if (IS_I18N) {
      const dateTime = new Date(opts.startDate);
      const [, gmt] = dateTime.toString().split('GMT');
      gmtTime = `(GMT${gmt.replace(/(\(|\))/g, '').replace(' ', ') ')
        .replace(/[0-9]{2}/, hour => `${hour}:`)}`;
    }
    tmpTime = `${opts.startDate.Format('yyyy/MM/dd hh:mm')}-${opts.endDate.Format('hh:mm')} ${gmtTime}`;
    if (opts.startDate.getDate() !== opts.endDate.getDate()) { // 不是同一天
      tmpTime = `${opts.startDate.Format('yyyy/MM/dd hh:mm')}-${opts.endDate.Format('yyyy/MM/dd hh:mm')} ${gmtTime}`;
    }
  }

  return tmpTime;
};

export const getICalendarDescription = (options = {}, t = v => v) => {
  const opts = dealICalendarParams(options);
  const tmpPassword = opts.password ? `\n${t('会议密码')}：${opts.password}` : '';
  const tmpPstnPassword = opts.pstnPassword ? `\n${t('数字密码')}：${opts.pstnPassword}` : '';
  const tmpLocation = opts.location ? `\n${t('会议地点')}：${opts.location}` : '';
  const tmpLiveUrl = opts.liveUrl ? `${t('直播链接')}：\n${opts.liveUrl}\n\n` : '';

  // 与会成员
  const inviteList = [
    // 会议发起人
    {
      english_name: opts.nickName,
      local_name: opts.chineseName,
    },
    ...options.inviteList,
  ];
  const tmpInviteList = getInviteList(opts.hostAppUid, inviteList, Number(opts.userType));

  // 会议时间
  const tmpTime = getICalendarDescMeetingTime({
    startDate: opts.startDate,
    endDate: opts.endDate,
    meetingType: opts.meetingType,
  });
  // 重复周期
  let _recurringRule = '\n';
  console.log('>>> get-ICalendar-description recurring-rule:', opts.meetingType, opts.recurringRule);
  if (opts.recurringRule && opts.meetingType === 1) {
    const repeatRateOptions = getRepeatRateOptions(opts.startDate);
    _recurringRule = `${t('重复周期')}：${repeatRateOptions[(opts.recurringRule.recurring_type || 0)]}\n\n`;
  }

  let _tmpUserName = dealLiName({
    englishName: opts.nickName,
    localName: opts.chineseName,
  });

  console.log('>>> oneDial:', opts.oneDial);
  if (typeof opts.oneDial === 'undefined') {
    return;
  }

  let _members = `\n${t('与会成员')}: ${tmpInviteList}`;
  if (Number(opts.userType) === 1) { // 帐号类型 1 个人用户 2 企业用户
    _tmpUserName = `${opts.nickName}`;
    _members = '';
  }

  const { showPhoneInfo = true } = options;
  let _phoneInfo = '';
  if (showPhoneInfo) {
    _phoneInfo = IS_I18N
      ? getICalendarDescPhoneInfoI18n(options.userPstnInfo, t) : getICalendarDescPhoneInfo(options, t);
  }

  console.log('>>> _tmpUserName:', !_tmpUserName, !tmpInviteList, tmpInviteList);
  let _title = t('{{_tmpUserName}} 邀请您参加腾讯会议\n', {
    _tmpUserName,
  });
  if (!_tmpUserName) {
    _title = '';
  }
  if (!tmpInviteList) {
    _members = '';
  }

  const description = `${_title
  }${t('会议主题')}：${opts.summary}\n`
    + `${t('会议时间')}：${tmpTime}\n${_recurringRule
    }${t('点击链接直接加入会议')}：\n${opts.url}\n\n`
    + `${tmpLiveUrl}`
    + `${t('会议 ID')}：${divideNum(opts.meetingCode)}${tmpPassword}${tmpPstnPassword}\n\n${_phoneInfo
    }${_members}`
    + `${tmpLocation}`;

  console.log('>>> get-iCalendar-description:\n', description, '\ntmp-pstn-password:', tmpPstnPassword);
  return description;
};

export const creatingICalendar = async (options = {}) => {
  const opts = dealICalendarParams(options);
  console.log('>>> creating-iCalendar2:', opts);
  window.ICAL = await loadIcalJs();
  const eventTimeObj = {
    startDate: new window.ICAL.Time({
      year: opts.startDate.getFullYear(),
      month: (opts.startDate.getMonth() + 1),
      day: opts.startDate.getDate(),
      hour: opts.startDate.getHours(),
      minute: opts.startDate.getMinutes(),
      // 写死 0，防止 outlook 四舍五入
      second: 0,
      isDate: false,
    }),
    endDate: new window.ICAL.Time({
      year: opts.endDate.getFullYear(),
      month: (opts.endDate.getMonth() + 1),
      day: opts.endDate.getDate(),
      hour: opts.endDate.getHours(),
      minute: opts.endDate.getMinutes(),
      // 写死 0，防止 outlook 四舍五入
      second: 0,
      isDate: false,
    }),
  };
  // 设置周期性
  let EXDATE = '';
  let RRULE = '';
  let modifyArr = [];
  let veventStr = '';
  // 0普通会议，1周期性会议，默认为0
  if (opts.meetingType === 1) {
    RRULE = `${getRRULE(opts.recurringRule, window.ICAL)}`;
    [EXDATE, modifyArr] = getRRULE_EXDATE(opts.recurringRule, window.ICAL, opts.subMeetings);
    veventStr = getVevent({
      modifyArr,
      url: opts.url,
      summary: opts.summary,
      inviteList: opts.inviteList,
      nickName: opts.nickName,
      email: opts.email,
    }, window.ICAL);
    console.log('>>> RRULE:', RRULE);
  }

  const compStr = `
BEGIN:VCALENDAR
METHOD:PUBLISH
PRODID:-//meeting.tencent.com//iCalendar
VERSION:2.0
BEGIN:VEVENT
DESCRIPTION:
DTSTART:${eventTimeObj.startDate.toICALString()}
DTEND:${eventTimeObj.endDate.toICALString()}
${getAttendee(opts.inviteList)}
${getOrganizer(opts.nickName, opts.email, opts.chineseName)}
CLASS:PUBLIC
PRIORITY:5
CREATED:${eventTimeObj.startDate.toICALString()}
DTSTAMP:${eventTimeObj.startDate.toICALString()}
TRANSP:OPAQUE
STATUS:CONFIRMED
SEQUENCE:1
LOCATION:${opts.url}
SUMMARY:${opts.summary}
${EXDATE}
${RRULE}
BEGIN:VALARM
TRIGGER:-PT10M
ACTION:DISPLAY
DESCRIPTION:Reminder
END:VALARM
X-MICROSOFT-CDO-APPT-SEQUENCE:1
X-MICROSOFT-CDO-OWNERAPPTID:-1
X-MICROSOFT-CDO-BUSYSTATUS:TENTATIVE
X-MICROSOFT-CDO-INTENDEDSTATUS:BUSY
X-MICROSOFT-CDO-ALLDAYEVENT:FALSE
X-MICROSOFT-CDO-IMPORTANCE:1
X-MICROSOFT-CDO-INSTTYPE:0
X-MICROSOFT-DONOTFORWARDMEETING:FALSE
X-MICROSOFT-DISALLOW-COUNTER:FALSE
END:VEVENT
${veventStr}
END:VCALENDAR
  `;

  const jcalData = window.ICAL.parse(compStr);
  const comp = new window.ICAL.Component(jcalData);
  // var vevent = comp.getFirstSubcomponent("vevent");
  const vevents = comp.getAllSubcomponents('vevent');
  console.log('>>> vevents:', vevents.length, modifyArr.length);
  for (let i = 0; i < vevents.length; i++) {
    let description = '';
    if (i === 0) {
      description = opts.inviteContent || getICalendarDescription(opts);
    } else {
      description = opts.inviteContent || getICalendarDescription({
        ...opts,
        startDate: new Date(modifyArr[i - 1].start_time * 1000),
        endDate: new Date(modifyArr[i - 1].end_time * 1000),
      });
    }
    const event = new window.ICAL.Event(vevents[i]);
    event.description = description;
  }

  downloadFile(`${opts.summary}.ics`, comp.toString());
};

export const getRRULE_UNTIL = (recurringRule, ICAL) => {
  let untilDate = recurringRule.until_date;
  // 计算“结束日期时间戳 (UTC 单位:秒)”
  if (
    Number(recurringRule.until_type) === 1
    && !recurringRule.until_date
  ) {
    untilDate = dealUntilDate(
      moment(recurringRule.first_start_time * 1000),
      recurringRule.until_times,
      recurringRule.recurring_type,
    );
  }

  const untilTime = moment(untilDate * 1000);
  const res = new ICAL.Time({
    year: untilTime.year(),
    month: untilTime.month() + 1,
    day: untilTime.date(),
    hour: untilTime.hour(),
    minute: untilTime.minute(),
    second: untilTime.second(),
    isDate: false,
  }).toICALString();
  console.log('>>> get-RRULE_UNTIL UNTIL:', res);

  return res;
};

export const getRRULE_WKST = (recurringRule) => {
  const firstStartTime = moment(recurringRule.first_start_time * 1000);

  const key = firstStartTime.weekday();
  switch (key) {
    case 0: // 周一
      return 'MO';
    case 1: // 周二
      return 'TU';
    case 2: // 周三
      return 'WE';
    case 3: // 周四
      return 'TU';
    case 4: // 周五
      return 'FR';
    case 5: // 周六
      return 'SA';
    case 6: // 周日
      return 'SU';
    default:
      console.info('>>> default:', key);
  }
};

export const getVevent = (options = {}, ICAL) => {
  console.log(getVevent, options);
  const modifyArr = options.modifyArr || [];
  if (
    isArray(modifyArr)
    && modifyArr.length === 0
  ) {
    return '';
  }

  const dealModifyArr = (obj) => {
    const startTime = moment(obj.start_time * 1000);
    const endTime = moment(obj.end_time * 1000);

    const startDate = new ICAL.Time({
      year: startTime.year(),
      month: startTime.month() + 1,
      day: startTime.date(),
      hour: startTime.hour(),
      minute: startTime.minute(),
      // 写死 0，防止 outlook 四舍五入
      second: 0,
      isDate: false,
    });
    const endDate = new ICAL.Time({
      year: endTime.year(),
      month: endTime.month() + 1,
      day: endTime.date(),
      hour: endTime.hour(),
      minute: endTime.minute(),
      // 写死 0，防止 outlook 四舍五入
      second: 0,
      isDate: false,
    });

    const tmp = `
BEGIN:VEVENT
DESCRIPTION:
DTSTART:${startDate.toICALString()}
DTEND:${endDate.toICALString()}
${getAttendee(options.inviteList)}
${getOrganizer(options.nickName, options.email, options.chineseName)}
CLASS:PUBLIC
PRIORITY:5
CREATED:${startDate.toICALString()}
DTSTAMP:${startDate.toICALString()}
TRANSP:OPAQUE
STATUS:CONFIRMED
SEQUENCE:1
LOCATION:${options.url}
SUMMARY:${options.summary}
BEGIN:VALARM
TRIGGER:-PT10M
ACTION:DISPLAY
DESCRIPTION:Reminder
END:VALARM
X-MICROSOFT-CDO-APPT-SEQUENCE:1
X-MICROSOFT-CDO-OWNERAPPTID:-1
X-MICROSOFT-CDO-BUSYSTATUS:TENTATIVE
X-MICROSOFT-CDO-INTENDEDSTATUS:BUSY
X-MICROSOFT-CDO-ALLDAYEVENT:FALSE
X-MICROSOFT-CDO-IMPORTANCE:1
X-MICROSOFT-CDO-INSTTYPE:0
X-MICROSOFT-DONOTFORWARDMEETING:FALSE
X-MICROSOFT-DISALLOW-COUNTER:FALSE
END:VEVENT
      `;

    return tmp;
  };

  let res = '';
  for (let i = 0; i < modifyArr.length; i++) {
    res += dealModifyArr(modifyArr[i]);
  }

  return res;
};

export const getRRULE_EXDATE = (recurringRule, ICAL, subMeetings) => {
  let EXDATE = '';
  const arr = createSubMeetings(recurringRule);

  const modifyArr = []; // 存储: 用户修改过 subMeetings[i]
  for (let i = 0; i < subMeetings.length; i++) {
    let modified = true;
    for (let t = 0; t < arr.length; t++) {
      if (
        subMeetings[i].start_time === arr[t].start_time
        && subMeetings[i].end_time === arr[t].end_time
      ) {
        // 0: 用户修改过该子会议，需要排除(默认); 1: 用户未修改过该子会议
        arr[t]._status = 1;
        modified = false;
        break;
      }
    }
    if (modified) { // 用户修改过 subMeetings[i]
      modifyArr.push(subMeetings[i]);
    }
  }

  const exdateArr = []; // RRULE 规则需要排除的子会议
  for (let i = 0; i < arr.length; i++) {
    if (arr[i]._status === 0) {
      const time = moment(arr[i].start_time * 1000);
      const tmp = new ICAL.Time({
        year: time.year(),
        month: time.month() + 1,
        day: time.date(),
        hour: time.hour(),
        minute: time.minute(),
        second: time.second(),
        isDate: false,
      }).toICALString();

      exdateArr.push(tmp);
    }
  }
  if (exdateArr.length > 0) {
    // EXDATE = `EXDATE;TZID=${moment.tz.guess()}:${exdateArr.join(',')}`;
    EXDATE = `EXDATE:${exdateArr.join(',')}`;
  }

  return [EXDATE, modifyArr];
};

export const getRRULE = (recurringRule, ICAL) => {
  let FREQ = 'DAILY';
  const WKST = getRRULE_WKST(recurringRule);
  let INTERVAL = '1';
  const UNTIL = getRRULE_UNTIL(recurringRule, ICAL);
  let BYDAY = '';

  switch (Number(recurringRule.recurring_type)) {
    case 0: // 每天
      FREQ = 'DAILY'; // 表示以天为间隔单位进行重复。
      break;
    case 1: // 每个工作日(即周一至周五)
      FREQ = 'WEEKLY';
      BYDAY = 'MO,TU,WE,TH,FR';
      break;
    case 2: // 每周
      FREQ = 'WEEKLY';
      break;
    case 3: // 每两周
      FREQ = 'WEEKLY';
      INTERVAL = '2';
      break;
    case 4: // 每月
      FREQ = 'MONTHLY';
      break;
    default:
      console.info('>>> default:', recurringRule.recurring_type);
  }

  let RRULE = `RRULE:FREQ=${FREQ};WKST=${WKST};UNTIL=${UNTIL};INTERVAL=${INTERVAL}`;
  if (BYDAY) {
    RRULE = `${RRULE};BYDAY=${BYDAY}`;
  }

  return RRULE;
};

/**
 * @typedef {Object} User
 * @property {String} app_id
 * @property {String} app_uid
 * @property {String} avatar_url
 * @property {String} email
 * @property {String} english_name
 * @property {String} local_name
 * @param {Array<User>} inviteList
 */

const getAttendee = inviteList => inviteList
  .filter(user => Boolean(user.email))
  .map((user) => {
    const cn = dealLiName({
      englishName: user.english_name,
      localName: user.local_name,
    });
    return `ATTENDEE;EMAIL="${user.email}";CN="${cn || user.email}";RSVP=TRUE:mailto:${user.email}`;
  })
  .join('\n');

/**
 * @param {String} nickName
 * @param {String} email
 * @param {String} localName
 */
const getOrganizer = (nickName, email, localName) => {
  if (!email) {
    return '';
  }
  const cn = dealLiName({
    englishName: nickName,
    localName,
  });
  return `ORGANIZER;EMAIL="${email}";CN="${cn || email}":mailto:${email}`;
};
